import type { Rule } from 'ant-design-vue/es/form';
import { normalizeSerial } from '@/utils/string';
import { GENERATION, SOLSHARE_VERSION } from '@/shared/constants/solshare';

const mk1Regex = /^(\dP_)?\d{2,3}A?_\d{4}(_[Tt][Ee][Ss][Tt])?$/;
const mk2Regex = /^[1-3]-[01]-\d{4}-[0-9](_[Tt][Ee][Ss][Tt])?$/;

export const INVALID_SOLSHARE_SERIAL_MESSAGE =
  'Invalid serial number, valid format: X-X-XXXX-X, 3P_35A_XXXX, 2P_100_XXXX';

/** Shown when only MK2 serials are accepted (e.g. commissioning until MK1 is supported). */
export const INVALID_SOLSHARE_SERIAL_MESSAGE_MK2_ONLY =
  'Invalid serial number, valid format: X-X-XXXX-X';

/**
 * Canonical serial for API / routing: MK2 keeps hyphens; MK1 uses underscore form (from {@link normalizeSerial}).
 */
export function normalizeSolshareSerialForSubmit(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;
  const upperNorm = normalizeSerialNumber(trimmed);
  if (mk2Regex.test(upperNorm)) {
    return upperNorm;
  }
  return normalizeSerial(trimmed);
}

export type SolshareSerialValidationOptions = {
  /**
   * When false, only MK2 serials are valid. Use for commissioning until MK1 commissioning is shipped.
   * @default true
   */
  allowMk1?: boolean;
};

/**
 * Validate the QR code content when scanning serial number.
 * @param qrcodeContent
 * @param options.allowMk1 When false, MK1 QR prefixes are rejected (same behaviour as commissioning manual entry).
 * @returns true if the QR code serial number is valid, false otherwise
 */
export const isValidQRCodeWithSerialNumber = (
  qrcodeContent: string,
  options: SolshareSerialValidationOptions = {},
) => {
  const allowMk1 = options.allowMk1 ?? true;
  const MK2_QRCODE_SERIAL_NUMBER_PREFIX = import.meta.env.VITE_MK2_QRCODE_SERIAL_NUMBER_PREFIX;
  const MK1_QRCODE_SERIAL_NUMBER_PREFIX = import.meta.env.VITE_MK1_QRCODE_SERIAL_NUMBER_PREFIX;

  const isMk1Prefix = qrcodeContent.startsWith(MK1_QRCODE_SERIAL_NUMBER_PREFIX);
  const isMk2Prefix = qrcodeContent.startsWith(MK2_QRCODE_SERIAL_NUMBER_PREFIX);

  if (!isMk1Prefix && !isMk2Prefix) {
    return false;
  }

  if (isMk1Prefix) {
    if (!allowMk1) {
      return false;
    }
    const serialNumber = qrcodeContent.substring(MK1_QRCODE_SERIAL_NUMBER_PREFIX.length);
    return mk1Regex.test(serialNumber);
  }

  if (isMk2Prefix) {
    const serialNumber = qrcodeContent.substring(MK2_QRCODE_SERIAL_NUMBER_PREFIX.length);
    return mk2Regex.test(serialNumber);
  }

  return false;
};

/**
 * Ant Design form validator: MK1 and/or MK2 depending on {@link SolshareSerialValidationOptions}.
 */
export function createSerialNumberValidator(options: SolshareSerialValidationOptions = {}) {
  const allowMk1 = options.allowMk1 ?? true;

  return (_rule: Rule, value: string) => {
    if (value === '') {
      return Promise.resolve();
    }

    const normalized = normalizeSolshareSerialForSubmit(value);
    const isMk1 = mk1Regex.test(normalized);
    const isMk2 = mk2Regex.test(normalized);

    if (isMk2) {
      return Promise.resolve();
    }
    if (allowMk1 && isMk1) {
      return Promise.resolve();
    }

    return Promise.reject(
      allowMk1 ? INVALID_SOLSHARE_SERIAL_MESSAGE : INVALID_SOLSHARE_SERIAL_MESSAGE_MK2_ONLY,
    );
  };
}

/**
 * Accepts MK1 and MK2 (SolCentre admin link, legacy callers).
 *
 * Commissioning should use {@link createSerialNumberValidator} with `allowMk1: false` or import from
 * `@/modules/commissioning/utils/solshare-commissioning`.
 */
export const serialNumberValidator = createSerialNumberValidator({ allowMk1: true });

/**
 * Get the SolShare version (Mk1 or Mk2)
 * @param serialNumber
 * @returns SOLSHARE_VERSION.Mk1 or SOLSHARE_VERSION.Mk2 or null
 */
export const getSolshareVersion = (serialNumber: string) => {
  if (mk1Regex.test(serialNumber)) {
    return SOLSHARE_VERSION.Mk1;
  } else if (mk2Regex.test(serialNumber)) {
    return SOLSHARE_VERSION.Mk2;
  }
  return null;
};

/**
 * Transform chars to uppercase (for MK1 usecase)
 * @param serialNumber
 * @returns serial number with uppercase chars and (optionally) lowercase'test' suffix
 */
export const normalizeSerialNumber = (serialNumber: string) => {
  return serialNumber.toUpperCase().replace('TEST', 'test');
};

/**
 * Get the Mk1 generation
 * @param serial - The serial number
 * @returns The generation (GENERATION.ONE or GENERATION.TWO) or null if the serial number is not a valid MK1 serial number
 */
export const getMk1Generation = (serial: string) => {
  const solshareVersion = getSolshareVersion(serial);

  if (solshareVersion === SOLSHARE_VERSION.Mk1) {
    const number = Number(serial.match(/\d{4}/)?.[0] ?? '0');
    if (serial.startsWith('2P')) {
      if (number < 120) {
        return GENERATION.ONE;
      } else {
        return GENERATION.TWO;
      }
    } else {
      if (number < 400) {
        return GENERATION.ONE;
      } else {
        return GENERATION.TWO;
      }
    }
  }

  return null;
};
